import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import type { CommentPopoverState, CanvasComment } from './canvas-types';
import { MessageSquare, X, Trash2, Check, Pencil } from 'lucide-react';

export interface CommentPinEditorProps {
  popover: CommentPopoverState;
  comments: CanvasComment[];
  addComment: (imageId:string,pinX:number,pinY:number,text:string)=>void;
  removeComment: (imageId:string,id:string)=>void;
  editComment: (id:string,text:string)=>void;
  setCommentPopover: (popover:CommentPopoverState|null)=>void;
  // Local image coordinates for editors inside a positioned preview, including
  // transformed dialogs. Canvas callers continue to use viewport coordinates.
  anchor?: { x:number; y:number; width:number; height:number };
}

const POPOVER_W = 260;
const OFFSET = 12;

export function CommentPinEditor({popover,addComment,removeComment,editComment,setCommentPopover,comments,anchor}:CommentPinEditorProps) {
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'list' | 'add'>('list');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState(34);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!anchor || !element) return;
    const measure = () => setEditorHeight(element.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [!!anchor]);

  const imageComments = comments.filter((c) => c.imageId === popover.imageId && c.isPending);
  const hasPending = imageComments.length > 0;

  useEffect(() => {
    if (!hasPending || mode === 'add') {
      setMode('add');
      setTimeout(() => inputRef.current?.focus({preventScroll:true}), 50);
    } else {
      setMode('list');
    }
  }, []);

  useEffect(() => {
    if (mode === 'add') {
      setTimeout(() => inputRef.current?.focus({preventScroll:true}), 50);
    }
  }, [mode]);

  // Click-outside the popover → save any draft text and close. Mouse-down
  // listener fires before click events so this beats accidentally clicking
  // through to another canvas element. Captures so the canvas's own
  // pane-click handler doesn't run first.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const el = containerRef.current;
      if (!el) return;
      if (el.contains(e.target as Node)) return;
      // Auto-save any in-progress text before closing
      const trimmed = text.trim();
      if (trimmed) {
        addComment(popover.imageId, popover.pinX, popover.pinY, trimmed);
      }
      setCommentPopover(null);
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [text, popover.imageId, popover.pinX, popover.pinY, addComment, setCommentPopover]);

  const computePosition = () => {
    if (anchor) {
      const width = Math.min(POPOVER_W, Math.max(0, anchor.width - 16));
      let left = anchor.x + OFFSET;
      let top = anchor.y - 40;
      if (left + width > anchor.width - 8) left = anchor.x - width - OFFSET;
      if (top < 8) top = anchor.y + OFFSET;
      return {
        left: Math.max(8, Math.min(left, anchor.width - width - 8)),
        top: Math.max(8, Math.min(top, anchor.height - editorHeight - 8)),
        width,
      };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = popover.screenX + OFFSET;
    let top = popover.screenY - 40;

    if (left + POPOVER_W > vw - 8) left = popover.screenX - POPOVER_W - OFFSET;
    if (left < 8) left = 8;
    if (top < 8) top = popover.screenY + OFFSET;
    if (top + 320 > vh - 8) top = vh - 328;
    return { left, top, width: POPOVER_W };
  };

  const pos = computePosition();

  const handleSubmit = () => {
    if (!text.trim()) return;
    addComment(popover.imageId, popover.pinX, popover.pinY, text.trim());
    setText('');
    setMode('list');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (mode === 'add' && hasPending) {
        setMode('list');
      } else {
        setCommentPopover(null);
      }
    }
  };

  const updatedComments = comments.filter((c) => c.imageId === popover.imageId && c.isPending);

  return (
    <div
      ref={containerRef}
      data-testid="canvas-comment-popover"
      className={`${anchor ? 'absolute' : 'fixed'} z-50`}
      style={pos}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {mode === 'list' && updatedComments.length > 0 ? (
        // Compact list view — borderless card with previous comments. Used
        // when the user clicks an existing pin to review/edit/delete.
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100">
          <div className="flex items-center gap-2 px-3 pt-2 pb-1.5">
            <MessageSquare className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
            <span className="text-xs font-medium text-gray-700">
              {updatedComments.length === 1 ? '1 comment' : `${updatedComments.length} comments`}
            </span>
            <button
              data-testid="canvas-comment-add-new"
              onClick={() => setMode('add')}
              className="ml-auto text-[10px] text-indigo-600 hover:text-indigo-500 font-medium"
            >
              + Add
            </button>
          </div>
          <div className="max-h-40 overflow-y-auto">
            {updatedComments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onDelete={() => removeComment(popover.imageId, comment.id)}
                onEdit={(newText) => editComment(comment.id, newText)}
              />
            ))}
          </div>
        </div>
      ) : (
        // Floating single-line input — borderless, no buttons. Enter saves,
        // Escape cancels, click-outside saves any in-progress text and
        // closes (handled by the useEffect above).
        <div className="bg-white rounded-full shadow-xl border border-gray-200 flex items-center px-3 py-1.5 gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
          <input
            ref={inputRef}
            data-testid="canvas-comment-input"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what to change…"
            className="min-w-0 flex-1 text-sm text-gray-800 placeholder-gray-400 bg-transparent border-0 outline-none focus:ring-0 p-0"
          />
        </div>
      )}
    </div>
  );
}

function CommentItem({ comment, onDelete, onEdit }: { comment: CanvasComment; onDelete: () => void; onEdit: (text: string) => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const editRef = useRef<HTMLTextAreaElement>(null);

  const startEdit = () => {
    setEditText(comment.text);
    setIsEditing(true);
    setTimeout(() => editRef.current?.focus(), 30);
  };

  const commitEdit = () => {
    const trimmed = editText.trim();
    if (trimmed && trimmed !== comment.text) onEdit(trimmed);
    setIsEditing(false);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitEdit(); }
    if (e.key === 'Escape') { setIsEditing(false); setEditText(comment.text); }
  };

  return (
    <div
      data-testid={`canvas-comment-item-${comment.id}`}
      className={`flex items-start gap-2 px-3 py-2 border-b border-gray-50 hover:bg-gray-50 group transition-colors ${comment.isFailed ? 'bg-red-50 hover:bg-red-50' : ''}`}
    >
      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${comment.isFailed ? 'bg-red-100' : 'bg-indigo-100'}`}>
        <span className={`text-[9px] font-bold ${comment.isFailed ? 'text-red-600' : 'text-indigo-600'}`}>
          {comment.author.charAt(0).toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <textarea
            ref={editRef}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={handleEditKeyDown}
            onBlur={commitEdit}
            rows={2}
            className="w-full text-xs text-gray-800 border border-indigo-300 rounded px-1.5 py-1 resize-none outline-none focus:ring-1 focus:ring-indigo-200"
          />
        ) : (
          <p
            data-testid={`canvas-comment-text-${comment.id}`}
            className="text-xs text-gray-800 leading-relaxed break-words cursor-text"
            onDoubleClick={startEdit}
          >{comment.text}</p>
        )}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-gray-400">{comment.author}</span>
          {comment.isFailed && (
            <span className="text-[10px] text-red-500 font-medium">• Failed — retry pending</span>
          )}
        </div>
      </div>
      {!isEditing && (
        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 flex-shrink-0 transition-all">
          <button
            data-testid={`canvas-comment-edit-${comment.id}`}
            onClick={startEdit}
            className="text-gray-400 hover:text-indigo-500"
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            data-testid={`canvas-comment-delete-${comment.id}`}
            onClick={onDelete}
            className="text-gray-400 hover:text-red-500"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
